import React from 'react';
import { Box, Button, Group, Text } from '@mantine/core';
import { IconUserSearch, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useStopImpersonation } from './useStopImpersonation';
import { ImpersonationData } from '@shared/types/user';

interface ImpersonationNoticeProps {
  impersonationData: ImpersonationData;
  onImpersonationStopped: () => void;
}

export function ImpersonationNotice({
  impersonationData,
  onImpersonationStopped,
}: ImpersonationNoticeProps) {
  const stopImpersonation = useStopImpersonation();

  if (
    !impersonationData?.isImpersonating ||
    !impersonationData.impersonatedUser
  ) {
    return null;
  }

  const handleStopImpersonation = () => {
    stopImpersonation.mutate(undefined, {
      onSuccess: () => {
        notifications.show({
          title: 'Impersonation Stopped',
          message: 'You have stopped impersonating the user',
          color: 'green',
        });
        onImpersonationStopped();
      },
      onError: error => {
        notifications.show({
          title: 'Error',
          message: error.message || 'Failed to stop impersonation',
          color: 'red',
        });
      },
    });
  };

  const { firstName, lastName, email } = impersonationData.impersonatedUser;

  return (
    <Box
      style={{
        backgroundColor: 'var(--mantine-color-orange-6)',
        borderBottom: '2px solid var(--mantine-color-orange-8)',
        color: 'var(--mantine-color-white)',
        padding: '4px 16px',
        fontSize: '14px',
        fontWeight: 600,
      }}
    >
      <Group justify="space-between" align="center" gap="md">
        <Group gap="xs" align="center">
          <IconUserSearch size={16} />
          <Text size="sm" fw={600} c="white">
            Impersonating: {firstName} {lastName} ({email})
          </Text>
        </Group>
        <Button
          variant="white"
          color="orange"
          size="xs"
          leftSection={<IconX size={12} />}
          onClick={handleStopImpersonation}
          loading={stopImpersonation.isPending}
          styles={{
            root: {
              height: '28px',
              fontSize: '12px',
              padding: '0 12px',
              fontWeight: 600,
            },
          }}
        >
          Stop
        </Button>
      </Group>
    </Box>
  );
}
